import { useMemo, useState } from 'react';
import { MetricGridView } from '@/components/views/MetricGridView';
import { useAppData } from '@/components/apps/useAppData';
import { recordTitle } from '@/components/apps/app-records-model';
import { cn } from '@/lib/utils';
import { outreachParameters } from '@shared/outreach';
import { OutreachProspects } from './OutreachProspects';
import { OutreachReview } from './OutreachReview';
import { OutreachSetup } from './OutreachSetup';
import { TableImportDrawer } from './TableImportDrawer';
import { useOutreach } from './useOutreach';

type Tab = 'prospects' | 'steps' | 'settings';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'prospects', label: 'Prospects' },
  { key: 'steps', label: 'E-mails' },
  { key: 'settings', label: 'Réglages' },
];

/**
 * The « sequence » block of an app (03/11/2026), any app whose objects are
 * companies or people: lemlist's campaign inside the app — its prospects
 * (the app's own list), its steps, its settings, and what came back.
 * Counted on replies, never on opens.
 */
export const OutreachSequenceBlock = ({ appId, canManage }: { appId: string; canManage: boolean }) => {
  const app = useAppData(appId);
  const data = useOutreach(appId);
  const [tab, setTab] = useState<Tab>('prospects');
  const [reviewing, setReviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const mine = data.mine;
  const toReview = mine.filter((enrollment) => enrollment.status === 'review');
  const writing = mine.filter((enrollment) => enrollment.status === 'drafting').length;

  const band = useMemo(() => ({
    presentation: 'band' as const,
    metrics: [
      { label: 'Contactés', value: String(mine.filter((enrollment) => enrollment.messages.some((message) => message.status === 'sent')).length) },
      { label: 'En cours', value: String(mine.filter((enrollment) => enrollment.status === 'active').length) },
      { label: 'Réponses', value: String(mine.filter((enrollment) => enrollment.status === 'replied').length) },
      { label: 'Refus', value: String(mine.filter((enrollment) => enrollment.status === 'opted_out').length) },
      { label: 'Adresses en erreur', value: String(mine.filter((enrollment) => enrollment.status === 'bounced').length) },
    ],
  }), [mine]);

  if (app.error || data.error) return <p role="alert" className="text-sm text-domino-inkSoft">{app.error ?? data.error}</p>;
  if (!app.list || !data.state) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-16 animate-pulse rounded-2xl bg-domino-warm" />
        <div className="h-64 animate-pulse rounded-xl bg-domino-warm" />
      </div>
    );
  }
  const { collection, records } = app.list;
  const titleOf = (recordId: string) => {
    const record = records.find((item) => item.id === recordId);
    return record ? recordTitle(record, collection) : '';
  };
  const personOf = (contactId: string) => data.state?.contacts.find((contact) => contact.id === contactId)?.fullName ?? '';
  // The parameters are the list's columns (hand-added ones flagged `manual`), then the person and the sender.
  const parameters = outreachParameters(collection.columns.map((column) => ({
    key: column.key, label: column.label, manual: Boolean((column as { manual?: boolean }).manual),
  })));
  const prospects = records.filter((record) => !record.archivedAt).flatMap((record) => {
    const contact = data.primaryOf(record.id);
    return contact ? [{
      recordId: record.id, title: recordTitle(record, collection), cells: record.computed,
      person: { fullName: contact.fullName, jobTitle: contact.jobTitle, email: contact.email },
    }] : [];
  });
  const senderName = app.members.find((member) => member.userId === data.userId)?.name ?? '';

  return (
    <div className="space-y-4">
      {mine.length > 0 && <MetricGridView data={band} hideTitle />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" className="flex gap-1 rounded-xl bg-domino-warm p-1">
          {TABS.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)}
              className={cn('rounded-lg px-3 py-1.5 text-sm', tab === item.key ? 'bg-domino-white font-semibold text-domino-ink shadow-sm' : 'text-domino-inkSoft hover:text-domino-ink')}>
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {canManage && (
          <button type="button" onClick={() => setImporting(true)}
            className="rounded-xl border border-domino-cardBorder bg-domino-white px-3 py-1.5 text-sm text-domino-ink hover:border-domino-aubergine">
            Importer un tableau
          </button>
        )}
        {(toReview.length > 0 || writing > 0) && (
          <button type="button" disabled={toReview.length === 0} onClick={() => setReviewing(true)}
            className="rounded-xl bg-domino-aubergine px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
            {toReview.length > 0 ? `Relire ${toReview.length}` : ''}{toReview.length > 0 && writing > 0 ? ' · ' : ''}{writing > 0 ? `${writing} en écriture` : ''}
          </button>
        )}
        </div>
      </div>

      {tab === 'prospects' && (
        <OutreachProspects toolAppId={appId} collection={collection} records={records} data={data}
          manualColumns={parameters.filter((parameter) => parameter.manual)}
          listColumns={collection.columns.slice(1).filter((column) => !(column as { manual?: boolean }).manual)}
          canManage={canManage} onCellSaved={() => { void app.reload(); }} />
      )}
      {tab !== 'prospects' && (
        <OutreachSetup toolAppId={appId} view={tab} steps={data.state.sequence?.steps ?? null} settings={data.state.sequence?.settings ?? null}
          parameters={parameters} prospects={prospects} senderName={senderName} canManage={canManage}
          onSaved={() => { void data.reload(); }} onParametersChanged={() => { void app.reload(); }} />
      )}

      {importing && <TableImportDrawer toolAppId={appId} onClose={() => setImporting(false)} onImported={() => { void app.reload(); void data.reload(); }} />}
      {reviewing && (
        <OutreachReview toolAppId={appId} enrollments={toReview} titleOf={titleOf} personOf={personOf}
          onClose={() => setReviewing(false)} onDone={() => { void data.reload(); }} />
      )}
    </div>
  );
};
